import { useEffect, useRef, useState, useCallback } from 'react'

export type WSStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'limited'

export interface WSStatusUpdate {
  type: 'status_update'
  channel: string
  event: {
    event_type: string
    timestamp: string
    data: Record<string, unknown>
  }
  request_id: string
}

function buildWsUrl(): string {
  const baseUrl = import.meta.env.VITE_API_URL || window.location.origin
  const wsBase = baseUrl.replace(/^http/, 'ws')
  const token = localStorage.getItem('access_token')
  if (!token) return ''
  return `${wsBase}/api/v1/live-status/ws?token=${token}`
}

export function useLiveStatusWebSocket(onStatusUpdate: (update: WSStatusUpdate) => void): WSStatus {
  const [status, setStatus] = useState<WSStatus>('disconnected')
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectDelay = useRef(1000)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const shouldConnect = useRef(true)
  const callbackRef = useRef(onStatusUpdate)
  callbackRef.current = onStatusUpdate

  const cleanup = useCallback(() => {
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null }
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null }
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      try { wsRef.current.close() } catch { /* ignore */ }
      wsRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!shouldConnect.current) return
    const url = buildWsUrl()
    if (!url) {
      setStatus('disconnected')
      reconnectTimer.current = setTimeout(() => connect(), 3000)
      return
    }

    try {
      setStatus(prev => prev === 'disconnected' ? 'connecting' : 'reconnecting')
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        setStatus('connected')
        reconnectDelay.current = 1000
      }

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data)
          if (msg.type === 'status_update') {
            callbackRef.current(msg as WSStatusUpdate)
          } else if (msg.type === 'heartbeat') {
            try { ws.send(JSON.stringify({ type: 'ping' })) } catch { /* ignore */ }
          } else if (msg.type === 'connected' && msg.subscribed_channels?.length) {
            try { ws.send(JSON.stringify({ type: 'subscribe', channels: msg.subscribed_channels })) } catch { /* ignore */ }
          }
        } catch { /* ignore parse errors */ }
      }

      ws.onerror = () => { /* onclose will handle reconnect */ }

      ws.onclose = (ev) => {
        wsRef.current = null
        if (!shouldConnect.current) { setStatus('disconnected'); return }
        const reason = ev.reason || ''
        if (reason.toLowerCase().includes('limit')) {
          setStatus('limited')
          shouldConnect.current = false
          return
        }
        setStatus('reconnecting')
        const baseDelay = Math.min(reconnectDelay.current, 30000)
        const jitteredDelay = Math.random() * baseDelay
        reconnectTimer.current = setTimeout(() => connect(), jitteredDelay)
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000)
      }
    } catch {
      setStatus('reconnecting')
      const baseDelay = Math.min(reconnectDelay.current, 30000)
      const jitteredDelay = Math.random() * baseDelay
      reconnectTimer.current = setTimeout(() => connect(), jitteredDelay)
      reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000)
    }
  }, [])

  useEffect(() => {
    shouldConnect.current = true
    let cancelled = false
    const timer = setTimeout(() => {
      if (!cancelled) connect()
    }, 0)
    return () => {
      cancelled = true
      clearTimeout(timer)
      cleanup()
    }
  }, [connect, cleanup])

  return status
}
